import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Demo admin for testing; officers are managed in OfficerIncharge and stored in localStorage
  const demoUsers = {
    admin: {
      id: 1,
      username: 'admin',
      password: 'admin123',
      name: 'Admin User',
      role: 'admin',
      email: 'admin@tnau.ac.in',
      phone: '+91 9876543210'
    }
  };

  const loadOfficers = () => {
    try {
      const raw = localStorage.getItem('officers');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    // Check for stored authentication using the enhanced verification
    const authStatus = authService.verifyStoredAuth();
    
    if (authStatus.isAuthenticated && authStatus.user) {
      setUser(authStatus.user);
      setIsAuthenticated(true);
      console.log('✅ User authenticated from stored data:', authStatus.user);
    } else {
      console.log('❌ No valid stored authentication found');
      // Clear any corrupted data
      authService.clearAuth();
    }
    
    setLoading(false);
  }, []);

  // Seed or migrate demo officers so demo logins always work
  useEffect(() => {
    try {
      const raw = localStorage.getItem('officers');
      const existing = raw ? JSON.parse(raw) : [];

      const demoOfficers = [
        { id: 101, code: 'OFF101', name: 'Dr. Rajesh Kumar', username: 'rajesh', password: 'demo123', email: 'rajesh.kumar@tnau.ac.in', phone: '+91 98765 41001', designation: 'Field Officer', status: 'active', joinDate: '2023-06-01', experience: '5 years' },
        { id: 102, code: 'OFF102', name: 'Dr. Priya Sharma', username: 'priya',  password: 'demo123', email: 'priya.sharma@tnau.ac.in',  phone: '+91 98765 41002', designation: 'Field Officer', status: 'active', joinDate: '2022-03-15', experience: '8 years' },
        { id: 103, code: 'OFF103', name: 'Dr. Amit Patel',   username: 'amit',   password: 'demo123', email: 'amit.patel@tnau.ac.in',   phone: '+91 98765 41003', designation: 'Field Officer', status: 'active', joinDate: '2021-09-10', experience: '6 years' },
        { id: 104, code: 'OFF104', name: 'Dr. Sangeeta Verma',username: 'sangeeta',password: 'demo123', email: 'sangeeta.verma@tnau.ac.in',phone: '+91 98765 41004', designation: 'Field Officer', status: 'active', joinDate: '2020-11-05', experience: '7 years' },
        { id: 105, code: 'OFF105', name: 'Dr. Ramesh Singh', username: 'ramesh', password: 'demo123', email: 'ramesh.singh@tnau.ac.in', phone: '+91 98765 41005', designation: 'Field Officer', status: 'active', joinDate: '2019-01-20', experience: '10 years' }
      ];

      // If officers exist but missing password, add default password
      let migrated = Array.isArray(existing) ? existing.map(o => ({ ...o, password: o.password || 'demo123' })) : [];

      // Ensure demo usernames exist (merge without duplicates by username)
      const byUsername = new Map(migrated.map(o => [o.username, o]));
      demoOfficers.forEach(d => {
        if (!byUsername.has(d.username)) {
          byUsername.set(d.username, d);
        }
      });
      const next = Array.from(byUsername.values());
      localStorage.setItem('officers', JSON.stringify(next));
    } catch {}
  }, []);

  const login = async (username, password) => {
    try {
      // Try API login first
      console.log('Attempting API login for user:', username);
      const response = await authService.login(username, password);
      console.log('AuthContext received login response:', response);
      
      // Handle API response format: { success: true, data: { user: {...}, token: "..." } }
      let userData;
      if (response && response.success && response.data && response.data.user) {
        userData = response.data.user;
        console.log('Extracted user data from API response:', userData);
      } else if (response && response.user) {
        userData = response.user;
        console.log('Extracted user data from direct response:', userData);
      } else {
        console.error('Invalid API response format:', response);
        throw new Error('Invalid API response format');
      }
      
      setUser(userData);
      setIsAuthenticated(true);
      console.log('User authenticated successfully:', userData);
      return response; // Return full response for Login component to handle
    } catch (error) {
      // Fallback to demo users if API is not available
      console.warn('API login failed, falling back to demo users:', error.message);
      
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // 1) Admin demo
        const demo = demoUsers[username];
        if (demo && demo.password === password) {
          const demoData = { ...demo };
          delete demoData.password;
          setUser(demoData);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(demoData));
          resolve(demoData);
          return;
        }

        // 2) Officers created in management (localStorage)
        const officers = loadOfficers();
        const match = officers.find((o) => o.username === username && o.password === password);
        if (match) {
          const officerUser = {
            id: match.id,
            username: match.username,
            name: match.name,
            email: match.email,
            phone: match.phone,
            role: 'officer' // dynamic roles determined by assignments
          };
          setUser(officerUser);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(officerUser));
          resolve(officerUser);
          return;
        }

        reject(new Error('Invalid credentials'));
      }, 1000);
    });
    }
  };

  const logout = async () => {
    console.log('Logout function called');
    try {
      // Try API logout first
      await authService.logout();
    } catch (error) {
      console.warn('API logout failed:', error.message);
    } finally {
    setUser(null);
    setIsAuthenticated(false);
    // Clear all authentication-related data
    localStorage.removeItem('user');
      localStorage.removeItem('token');
    localStorage.removeItem('location');
    // Clear any other session data that might exist
    sessionStorage.clear();
    // Force a page reload to ensure complete cleanup
    window.location.href = '/login';
    }
  };

  const forgotPassword = async (email) => {
    try {
      // Try API forgot password first
      const response = await authService.forgotPassword(email);
      return response;
    } catch (error) {
      // Fallback to demo implementation if API is not available
      console.warn('API forgot password failed, falling back to demo:', error.message);
      
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Generate a 6-digit reset code
        const resetCode = Math.floor(100000 + Math.random() * 900000).toString();
        
        // Store the reset code and email in localStorage for demo purposes
        // In a real app, this would be stored in a database
        const resetData = {
          email,
          code: resetCode,
          timestamp: Date.now(),
          expiresAt: Date.now() + (15 * 60 * 1000) // 15 minutes expiry
        };
        
        localStorage.setItem('passwordReset', JSON.stringify(resetData));
        
        // Simulate email sending
        console.log(`Password reset code ${resetCode} sent to ${email}`);
        
        resolve({ 
          message: `Reset code sent to ${email}`,
          code: resetCode // In demo, we'll show the code. In real app, this would be hidden
        });
      }, 1000);
    });
    }
  };

  const verifyResetCode = async (email, code) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const resetData = localStorage.getItem('passwordReset');
          if (!resetData) {
            reject(new Error('No reset request found'));
            return;
          }
          
          const data = JSON.parse(resetData);
          
          // Check if email matches and code is correct
          if (data.email !== email || data.code !== code) {
            reject(new Error('Invalid reset code'));
            return;
          }
          
          // Check if code has expired
          if (Date.now() > data.expiresAt) {
            localStorage.removeItem('passwordReset');
            reject(new Error('Reset code has expired'));
            return;
          }
          
          resolve({ message: 'Code verified successfully' });
        } catch (error) {
          reject(new Error('Invalid reset data'));
        }
      }, 500);
    });
  };

  const resetPassword = async (email, code, newPassword) => {
    try {
      // Try API reset password first
      const response = await authService.resetPassword(email, code, newPassword);
      return response;
    } catch (error) {
      // Fallback to demo implementation if API is not available
      console.warn('API reset password failed, falling back to demo:', error.message);
      
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const resetData = localStorage.getItem('passwordReset');
          if (!resetData) {
            reject(new Error('No reset request found'));
            return;
          }
          
          const data = JSON.parse(resetData);
          
          // Verify code again
          if (data.email !== email || data.code !== code) {
            reject(new Error('Invalid reset code'));
            return;
          }
          
          if (Date.now() > data.expiresAt) {
            localStorage.removeItem('passwordReset');
            reject(new Error('Reset code has expired'));
            return;
          }
          
          // Update password in demo users or officers
          const demo = Object.values(demoUsers).find(u => u.email === email);
          if (demo) {
            demo.password = newPassword;
            // In a real app, this would update the database
          } else {
            // Check officers
            const officers = loadOfficers();
            const officerIndex = officers.findIndex(o => o.email === email);
            if (officerIndex !== -1) {
              officers[officerIndex].password = newPassword;
              localStorage.setItem('officers', JSON.stringify(officers));
            }
          }
          
          // Clear reset data
          localStorage.removeItem('passwordReset');
          
          resolve({ message: 'Password reset successfully' });
        } catch (error) {
          reject(new Error('Failed to reset password'));
        }
      }, 1000);
    });
    }
  };

  // Get current user profile from API
  const getProfile = async () => {
    try {
      // Try API get profile first (GET /auth/profile → { success, data: { ...user } })
      const response = await authService.getProfile();
      const profileUser =
        response && response.data != null && typeof response.data === 'object'
          ? response.data
          : response?.user || response;
      if (!profileUser || typeof profileUser !== 'object') {
        throw new Error('Invalid profile response');
      }
      setUser(profileUser);
      localStorage.setItem('user', JSON.stringify(profileUser));
      return { ...response, user: profileUser };
    } catch (error) {
      // Fallback to localStorage if API is not available
      console.warn('API get profile failed, falling back to localStorage:', error.message);
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return { user: userData, success: true };
      }
      throw new Error('Failed to get profile');
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      // Try API update profile first
      const response = await authService.updateProfile(profileData);
      // Update local user state with updated data
      const updatedUser =
        response && response.data != null && typeof response.data === 'object'
          ? response.data
          : response?.user;
      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else if (response?.success) {
        setUser((prev) => {
          const {
            currentPassword: _c,
            newPassword: _n,
            confirmPassword: _x,
            ...safeFields
          } = profileData || {};
          const merged = { ...(prev || {}), ...safeFields };
          localStorage.setItem('user', JSON.stringify(merged));
          return merged;
        });
      }
      return response;
    } catch (error) {
      // Fallback to local update if API is not available
      console.warn('API update profile failed, falling back to local update:', error.message);
      
      // Update local user data
      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return { 
        success: true, 
        message: 'Profile updated locally (API unavailable)',
        user: updatedUser 
      };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    getProfile,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 